import React from 'react';
import {
  Box, Button, Typography, Chip, CircularProgress, Alert, Paper,
} from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import PsychologyRoundedIcon from '@mui/icons-material/PsychologyRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';

export default function Phase3GPTProfile({
  profile,
  selectedExperts,
  loading,
  error,
  onConfirm,
  onBack,
}) {
  if (loading) {
    return (
      <div className="gpt-loading">
        <CircularProgress size={48} />
        <div className="gpt-loading-text">
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            GPT-profiel samenstellen...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We combineren de inzichten van {selectedExperts?.length || 0} experts
            <br />tot een uniek en krachtig GPT-profiel.
          </Typography>
        </div>
        <div className="gpt-loading-dots">
          <span>•</span><span>•</span><span>•</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gpt-phase">
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button startIcon={<ArrowBackRoundedIcon />} onClick={onBack}>Terug</Button>
      </div>
    );
  }

  if (!profile) return null;

  return (
    <div className="gpt-phase">
      <div className="gpt-phase-header">
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <SmartToyRoundedIcon sx={{ color: '#6366f1' }} />
          Voorgesteld GPT-profiel
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Bekijk het voorgestelde profiel en ga verder naar de bronverdieping.
        </Typography>
      </div>

      {/* GPT Name & Description */}
      <Paper sx={{ p: 3, mb: 2, background: 'linear-gradient(135deg, #eef2ff 0%, #faf5ff 100%)', border: '1px solid #c7d2fe' }}>
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#4f46e5', mb: 1 }}>
          {profile.naam}
        </Typography>
        <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6 }}>
          {profile.rolBeschrijving}
        </Typography>
      </Paper>

      {/* Two column layout */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
        {/* Kern Expertise */}
        <div className="gpt-output-section">
          <h3>
            <PsychologyRoundedIcon sx={{ color: '#6366f1', fontSize: 20 }} />
            Kernexpertise
          </h3>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {(profile.kernExpertise || []).map((item, i) => (
              <Chip key={i} label={item} color="primary" variant="outlined" size="small" />
            ))}
          </Box>
        </div>

        {/* Tone of Voice */}
        <div className="gpt-output-section">
          <h3>
            <PersonRoundedIcon sx={{ color: '#0d9488', fontSize: 20 }} />
            Tone of Voice
          </h3>
          <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
            {profile.toneOfVoice}
          </Typography>
        </div>
      </Box>

      {/* Denkkaders */}
      <div className="gpt-output-section">
        <h3>
          <PsychologyRoundedIcon sx={{ color: '#f59e0b', fontSize: 20 }} />
          Denkkaders & Frameworks
        </h3>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {(profile.denkKaders || []).map((item, i) => (
            <Chip
              key={i}
              label={item}
              size="small"
              sx={{ backgroundColor: '#fef3c7', color: '#92400e', fontWeight: 600 }}
            />
          ))}
        </Box>
      </div>

      {/* Typische Taken */}
      <div className="gpt-output-section">
        <h3>
          <TaskAltRoundedIcon sx={{ color: '#10b981', fontSize: 20 }} />
          Typische taken
        </h3>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
          {(profile.typischeTaken || []).map((taak, i) => (
            <Box
              key={i}
              sx={{
                p: 1.5,
                borderRadius: 2,
                background: '#ecfdf5',
                border: '1px solid #a7f3d0',
                fontSize: '0.85rem',
                color: '#065f46',
              }}
            >
              {taak}
            </Box>
          ))}
        </Box>
      </div>

      {/* Onderscheidend */}
      <div className="gpt-output-section">
        <h3>Wat maakt deze GPT uniek?</h3>
        <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
          {profile.onderscheidend}
        </Typography>
      </div>

      {/* Expert Fundament */}
      <div className="gpt-output-section">
        <h3>Expert-fundament</h3>
        <Typography variant="body2" sx={{ lineHeight: 1.6, fontStyle: 'italic' }}>
          {profile.expertFundament}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
          {selectedExperts?.map((e, i) => (
            <Chip key={i} label={e.naam} size="small" color="primary" />
          ))}
        </Box>
      </div>

      {/* Voor wie & Wanneer */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
        <div className="gpt-output-section">
          <h3>Voor wie?</h3>
          <Typography variant="body2">{profile.voorWie}</Typography>
        </div>
        <div className="gpt-output-section">
          <h3>Wanneer inzetten?</h3>
          <Typography variant="body2">{profile.wanneerInzetten}</Typography>
        </div>
      </Box>

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button startIcon={<ArrowBackRoundedIcon />} onClick={onBack}>
          Terug
        </Button>
        <Button
          variant="contained"
          size="large"
          endIcon={<ArrowForwardRoundedIcon />}
          onClick={onConfirm}
          sx={{ px: 4 }}
        >
          Bronnen verdiepen
        </Button>
      </Box>
    </div>
  );
}
